import { useEffect, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Permission, SessionUser } from '@idas/shared';
import { api, ApiError } from './lib/api';
import { useConsoleStore } from './store/console.store';
import { ConsolePage } from './pages/ConsolePage';
import { DashboardDetailPage } from './pages/DashboardDetailPage';
import { DashboardsPage } from './pages/DashboardsPage';
import { HistoryPage } from './pages/HistoryPage';
import { AuditPage } from './pages/AuditPage';
import { AdminPage } from './pages/AdminPage';
import { AuthErrorPage } from './pages/AuthErrorPage';
import { AccessRequestPage } from './pages/AccessRequestPage';
import { SignInGate } from './components/SignInGate';
import { AppShell } from './components/AppShell';
import { FullPageSpinner } from './components/FullPageSpinner';

/**
 * Where a user lands, and where they are bounced back to.
 *
 * An auditor holds no `query:run`, so sending them to the console would show a
 * page whose every request 403s. Routing from capability rather than assuming
 * everyone is an analyst keeps each role's landing page one they can use.
 */
function defaultRouteFor(user: SessionUser): string {
  if (user.permissions.includes('query:run')) return '/console';
  if (user.permissions.includes('audit:read')) return '/audit';
  if (user.permissions.includes('user:read')) return '/admin';
  return '/history';
}

function Guarded({
  user,
  permission,
  element,
}: {
  user: SessionUser;
  permission: Permission;
  element: ReactElement;
}) {
  if (!user.permissions.includes(permission)) {
    return <Navigate to={defaultRouteFor(user)} replace />;
  }
  return element;
}

export function App() {
  const theme = useConsoleStore((s) => s.theme);

  // The dark class drives every colour token, so it belongs on <html> rather
  // than on a wrapper — otherwise the page background outside the app root
  // stays the wrong colour during scroll bounce.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const session = useQuery({
    queryKey: ['session'],
    queryFn: api.auth.me,
    retry: false,
    // Sessions have a server-side idle timeout; re-checking on focus means a
    // user returning to a stale tab is told immediately rather than at their
    // next query.
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60_000,
  });

  return (
    <Routes>
      {/* Reachable without a session: where a failed handshake lands. */}
      <Route path="/auth/error" element={<AuthErrorPage />} />
      {/* Also public — the visitor has no session precisely because they are
          waiting for one to be approved. */}
      <Route path="/access-request" element={<AccessRequestPage />} />

      <Route
        path="*"
        element={
          session.isLoading ? (
            <FullPageSpinner label="Checking your session…" />
          ) : session.error instanceof ApiError ? (
            <SignInGate error={session.error} />
          ) : session.data ? (
            <AppShell user={session.data}>
              <Routes>
                <Route
                  path="/console"
                  element={
                    <Guarded
                      user={session.data}
                      permission="query:run"
                      element={<ConsolePage user={session.data} />}
                    />
                  }
                />
                <Route
                  path="/dashboards"
                  element={
                    <Guarded
                      user={session.data}
                      permission="query:run"
                      element={<DashboardsPage user={session.data} />}
                    />
                  }
                />
                <Route
                  path="/dashboards/:id"
                  element={
                    <Guarded
                      user={session.data}
                      permission="query:run"
                      element={<DashboardDetailPage user={session.data} />}
                    />
                  }
                />
                <Route path="/history" element={<HistoryPage user={session.data} />} />
                <Route
                  path="/audit"
                  element={
                    <Guarded
                      user={session.data}
                      permission="audit:read"
                      element={<AuditPage user={session.data} />}
                    />
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <Guarded
                      user={session.data}
                      permission="user:read"
                      element={<AdminPage user={session.data} />}
                    />
                  }
                />
                <Route path="*" element={<Navigate to={defaultRouteFor(session.data)} replace />} />
              </Routes>
            </AppShell>
          ) : (
            <FullPageSpinner label="Loading…" />
          )
        }
      />
    </Routes>
  );
}
